import * as THREE from "three";
import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";

export default function AnimatedLights({
  isNight,
  wedgeTier = 1,   // 3=1.7s, 2=1.2s, 1=0.5s
  stepSize = 0.25,
  yReset = 6.5,
}) {
  const ambientRef = useRef(null);
  const sunRef = useRef(null);

  // Allocate vectors ONCE
  const dayPos = useMemo(() => new THREE.Vector3(0, 0, -5), []);
  const nightPos = useMemo(() => new THREE.Vector3(-2, 3.5, -1), []);
  const targetPos = useMemo(() => new THREE.Vector3(0, 0.6, 0), []);
  const targetSunPos = useMemo(() => new THREE.Vector3(), []);

  // Tick-down Y state
  const yRef = useRef(6);
  const yTargetRef = useRef(6);
  const tickAccRef = useRef(0);
  const prevNightRef = useRef(isNight);

  // Intensity state
  const sunIntRef = useRef(1.2);
  const ambIntRef = useRef(0.75);

  // Pick interval
  // Put your real “3,2,1 wedge” speeds here:
  const TIER1_SEC = 0.7;  // shortest wedge
  const TIER2_SEC = 1.25;  // medium
  const TIER3_SEC = 1.8;  // longest

  const tickIntervalSec =
    wedgeTier === 1 ? TIER1_SEC :
    wedgeTier === 2 ? TIER2_SEC :
    TIER3_SEC;

  // ---- Shadow smoothness + perf ----
  // We keep autoUpdate OFF, but we re-render shadows while moving.
  const prevSunPos = useRef(new THREE.Vector3(999, 999, 999));
  const prevSunInt = useRef(999);
  const prevAmbInt = useRef(999);

  useEffect(() => {
    const sun = sunRef.current;
    if (!sun) return;
    sun.shadow.autoUpdate = false;
    sun.shadow.needsUpdate = true; // first frame
  }, []);

  useFrame((_, delta) => {
    const sun = sunRef.current;
    const amb = ambientRef.current;
    if (!sun || !amb) return;

    const wasNight = prevNightRef.current;

    // Intensity targets
    const targetSunIntensity = isNight ? 0.55 : 1.25;
    const targetAmbIntensity = isNight ? 0.35 : 0.75;

    // Leaving night: reset Y + restart ticking
    if (wasNight && !isNight) {
      yTargetRef.current = yReset;
      tickAccRef.current = 0;
    }

    if (isNight) {
      yTargetRef.current = 0;
    } else {
      tickAccRef.current += delta;
      if (tickAccRef.current >= tickIntervalSec) {
        tickAccRef.current -= tickIntervalSec;
        yTargetRef.current = Math.max(0, yTargetRef.current - stepSize);
      }
    }

    // ---- EASED Y ----
    let ySpeed = 3.0;
    if (wasNight && !isNight) ySpeed = 10.0;
    if (isNight) ySpeed = 6.0;

    const kY = 1 - Math.exp(-ySpeed * delta);
    yRef.current = THREE.MathUtils.lerp(yRef.current, yTargetRef.current, kY);

    // Build targetSunPos with eased Y (X/Z from endpoints)
    const base = isNight ? nightPos : dayPos;
    targetSunPos.set(base.x, yRef.current, base.z);

    // ---- EASED follow + intensities ----
    const followSpeed = 6.0;
    const k = 1 - Math.exp(-followSpeed * delta);

    sunIntRef.current = THREE.MathUtils.lerp(sunIntRef.current, targetSunIntensity, k);
    ambIntRef.current = THREE.MathUtils.lerp(ambIntRef.current, targetAmbIntensity, k);

    sun.position.lerp(targetSunPos, k);
    sun.intensity = sunIntRef.current;
    amb.intensity = ambIntRef.current;

    // Aim at crib
    sun.target.position.copy(targetPos);
    sun.target.updateMatrixWorld();

    // ✅ KEY FIX: update shadow map while the light/intensity is still changing
    const posDelta = sun.position.distanceTo(prevSunPos.current);
    const sunIntDelta = Math.abs(sun.intensity - prevSunInt.current);
    const ambIntDelta = Math.abs(amb.intensity - prevAmbInt.current);

    // thresholds: tiny changes don’t trigger expensive shadow re-render
    const moving = posDelta > 0.002 || sunIntDelta > 0.002 || ambIntDelta > 0.002;

    if (moving) {
      sun.shadow.needsUpdate = true;
      prevSunPos.current.copy(sun.position);
      prevSunInt.current = sun.intensity;
      prevAmbInt.current = amb.intensity;
    }

    prevNightRef.current = isNight;
  });

  return (
    <>
      <ambientLight ref={ambientRef} intensity={0.75} />

      <directionalLight
        ref={sunRef}
        position={[0, 6, -6]}
        intensity={1.2}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-near={0.1}
        shadow-camera-far={30}
        shadow-camera-left={-6}
        shadow-camera-right={6}
        shadow-camera-top={6}
        shadow-camera-bottom={-6}
      />

      {sunRef.current && <primitive object={sunRef.current.target} />}
    </>
  );
}